import React, { useRef, useState, useEffect, useMemo, useCallback, Suspense, Fragment } from "react";
import { Icon, Dropdown, Input, InputProps, DropdownProps, List, Menu, Checkbox, Form } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { useSearchPerson } from "../../../data/persons/useSearchPerson";
import { IPerson } from "../../../models/IPerson";
import { useBool } from "@sg-core/index"
import { useOnClickOutside } from "@sg-core/index";
import { TPickerValue } from "../../../models/IHprPicker";
import { PersonNameById } from "../PersonName";
import { PersonSearchListItem } from "./PersonSearchListItem";
import { PersonSearchMenuContent } from "./PersonSearchMenuContent";
import { usePersonCertValidator } from "../../../hooks/usePersonCertValidator";
import { PersonCertStatus } from "../PersonCertStatus";
import { usePerson } from "../../../data/persons/usePerson";
import _debounce from "lodash/debounce";
import useSWR from "swr";
import "./_PersonSearch.less";


interface PersonSearchProps extends Omit<DropdownProps, "onChange" | "value"> {
    onChange: (value: TPickerValue) => void;
    value?: TPickerValue;
    isMulti?: boolean;
    onAddPerson?: () => void;
    onEditPerson?: (id: IPerson) => void;
    requiredCerts: Array<string>;
    showNotifyBy?: "phone" | "email" | "phone_and_email",
    excludeUncertified?: boolean;
};

const PersonSearch: React.FC<PersonSearchProps> = ({
    value,
    onChange,
    isMulti = false,
    clearable = true,
    onAddPerson,
    onEditPerson,
    requiredCerts,
    showNotifyBy,
    excludeUncertified = false,
    ...dropdownProps
}) => {

    const t = useTranslation().t;
    const texts = {
        typeToSeach: t("Kirjoita hakeaksesi"),
        noResults: t("Ei tuloksia..."),
        addNew: t("Lisää uusi"),
        searchError: t("Haku virhe!"),
        selected: t("Valittuna"),
        editPersonsDetail: t("Muokkaa käyttäjän tietoja")
    };

    const { search } = useSearchPerson();


    const wrapperElement = useRef<any>();
    const inputElement = useRef<any>();

    const [isOpen, open, close] = useBool();

    const [searchInput, setSearchInput] = useState<string>();
    const [query, setQuery] = useState<string>();
    const searchQuery = isOpen ? query || " " : query;


    const getValidValue = (value: TPickerValue) => isMulti ? Array.isArray(value) ? value : [] : value || null;
    const selected = getValidValue(value);
    const hasValue = Array.isArray(selected) ? selected.length : selected;
    const selectedCount = Array.isArray(selected) ? selected.length : selected ? 1 : 0;


    const debouncedSetquery = useCallback(_debounce(setQuery, 300), []);

    const { validatePersonCerts } = usePersonCertValidator(requiredCerts);
    const { data: singleSelectedPerson } = usePerson(!Array.isArray(selected) ? selected : null);
    const singleSelectedPersonCertStatus = singleSelectedPerson ? validatePersonCerts(singleSelectedPerson.certs) : undefined;




    useEffect(() => { debouncedSetquery(searchInput); }, [searchInput, debouncedSetquery]);

    useEffect(() => {
        if (isOpen) {
            inputElement.current?.focus();
        }
    }, [isOpen]);

    const fetcher = async (key: string): Promise<Array<IPerson>> => {
        const query = key.split("-").reverse()[0]; // eslint-disable-line

        if (!query) { return []; };


        /// showOnlyValidPersons
        const result = await search(query, excludeUncertified ? requiredCerts : undefined);
        if (result.isFailure) {
            throw result.error
        };

        return result.value
    };


    const { data, isValidating: searching, error: searchError } = useSWR(
        `PersonSearch_query-${requiredCerts.join("-")}-${searchQuery}`,
        fetcher,
        {
            suspense: false,
            dedupingInterval: 300
        }
    );


    const resultsWithoutSelected = useMemo(() => {
        return (data || []).filter((item) => Array.isArray(selected) ? !selected.includes(item.id) : selected !== item.id)
    }, [data, selected]);

    const closeResults = useCallback(() => {
        if (isOpen) {
            close();
            setSearchInput("");
        }
    }, [close, isOpen]);


    const updateValue = (value: TPickerValue) => {
        onChange(value);
        if (!isMulti) { closeResults(); };

        if (searchInput) { setSearchInput("") };

        const neutralizedValue = Array.isArray(value) ? value : value ? [value] : [];
        const unSelectedResults = (data || []).filter((res) => !neutralizedValue.includes(res.id));

        if (!unSelectedResults.length) { setSearchInput("") }
    };

    const selectPerson = (itemId: number) => {
        if (isMulti) {
            if (!Array.isArray(selected)) throw Error("Selected is not array");
            const isSelected = selected.indexOf(itemId) > -1;
            updateValue(isSelected ? selected.filter((id) => id !== itemId) : [...selected, itemId]);
        } else {
            updateValue(itemId)
        };
    };


    const selectResult = (itemId: number) => {
        selectPerson(itemId);
        if (isMulti) {
            inputElement.current?.focus();
        }
    };

    const _onClear = () => updateValue(isMulti ? [] : null);

    const _onInputChange: InputProps["onChange"] = (event, data) => {
        let newQuery = data.value;
        if (hasValue && isMulti && !searchInput?.length) {
            newQuery = newQuery.slice(-1);
        }
        //@ts-ignore
        if ((event.key === "Backspace" || event.nativeEvent.inputType === "deleteContentBackward") && !searchInput?.length) {
            return
        }
        if (!isOpen) {
            open()
        }
        setSearchInput(newQuery);
    };

    const _onInputKeyDown: InputProps["onKeyDown"] = (event: any, data: any) => {
        if (event.key === " ") {
            _onInputChange(event, { ...data, value: event.target.value + " " }); // Fixes wierd bug, where spacebar is dosn't fire inputs onChange handler
            return
        } else if (event.key === "Backspace" && !searchInput?.length) {
            if (hasValue) {
                if (isMulti) {
                    Array.isArray(value) && updateValue(value.splice(0, value.length - 1))
                } else {
                    updateValue(undefined)
                }
            }
        };
    };

    const _onInputFocus: InputProps["onFocus"] = async () => {
        open();
    };

    useOnClickOutside(wrapperElement, closeResults);


    return (
        <div className="PersonSearch-container">
            <div ref={wrapperElement} >

                <Dropdown
                    fluid
                    open={isOpen}
                    icon={null}
                    trigger={(
                        <div className="PersonSearch-value-container">
                            <Input
                                className="PersonSearch-input"
                                loading={searching && isOpen}
                                icon="angle down"
                                ref={inputElement}
                                fluid
                                onFocus={_onInputFocus}
                                onChange={_onInputChange}
                                onKeyDown={_onInputKeyDown}
                                value={isOpen ? searchInput : ""}
                                placeholder={!hasValue ? (
                                    isOpen ? texts.typeToSeach : dropdownProps.placeholder || texts.typeToSeach
                                ) : undefined}
                            />

                            {clearable && hasValue ? (
                                <div className="PersonSearch-clear">
                                    <Icon
                                        name="times"
                                        onClick={_onClear}
                                    />
                                </div>
                            ) : null}

                            {!isOpen && hasValue ? (
                                <div className="PersonSearch-selected">
                                    {selected && !Array.isArray(selected) ? (
                                        <Fragment>
                                            <PersonNameById
                                                statusIcon={singleSelectedPersonCertStatus && <PersonCertStatus status={singleSelectedPersonCertStatus} />}
                                                personId={selected as any}
                                                showCompany
                                            />
                                        </Fragment>
                                    ) : isMulti ? (
                                        `Valittuna ${selectedCount} henkilö`
                                    ) : null}
                                </div>
                            ) : null}

                        </div>
                    )}
                    {...dropdownProps}
                >
                    <Dropdown.Menu className="PersonSearch-dropdown-menu">
                        <Suspense fallback={<div />}>
                            <PersonSearchMenuContent
                                searchError={searchError}
                                searching={searching && Boolean(!data)}
                                onResultSelect={selectResult}
                                onAddPerson={onAddPerson}
                                results={resultsWithoutSelected}
                                certValidator={validatePersonCerts}
                                showNotifyBy={showNotifyBy}
                            />
                            {selected && !isMulti && singleSelectedPerson ? (
                                <Menu.Item
                                    onClick={() => onEditPerson && onEditPerson(singleSelectedPerson)}
                                    content={texts.editPersonsDetail}
                                />
                            ) : null}
                        </Suspense>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {
                isMulti && Array.isArray(selected) && selected.length ? (
                    <List className="PersonSearch-list">
                        {selected.map((personId) => (
                            <Suspense fallback={<div />}>
                                <PersonSearchListItem
                                    key={personId}
                                    personId={personId}
                                    onUnselect={selectPerson}
                                    onEditPerson={onEditPerson}
                                    certValidator={validatePersonCerts}
                                />
                            </Suspense>
                        ))}
                    </List>
                ) : null
            }
        </div >
    );
};


export default PersonSearch;
